import React, { useCallback, useRef, useState } from "react";
import Markdown from "@/components/Markdown";
import PropertiesPanel from "@/components/PropertiesPanel";
import { getCaretCoordinates } from "@/lib/markdownUtils";

/**
 * Split live-preview Markdown editor for the Vault.
 * Left: monospace source with [[wiki-link]] autocomplete.
 * Right: live-rendered preview (Properties panel + Markdown, incl. callouts/embeds).
 */
export default function MarkdownEditor({ value, onChange, docs = [], onOpenDoc }) {
  const taRef = useRef(null);
  const [ac, setAc] = useState(null); // { query, options, index, openPos, top, left }

  const updateAutocomplete = useCallback(() => {
    const el = taRef.current;
    if (!el) return;
    const caret = el.selectionStart;
    const before = value.slice(0, caret);
    const open = before.lastIndexOf("[[");
    const close = before.lastIndexOf("]]");
    if (open === -1 || open < close) { setAc(null); return; }
    const between = before.slice(open + 2);
    if (between.includes("\n") || between.includes("]") || between.includes("[")) { setAc(null); return; }
    const q = between.toLowerCase().trim();
    const options = (docs || [])
      .filter((d) => d.name && d.name.toLowerCase().includes(q))
      .slice(0, 8);
    if (options.length === 0) { setAc(null); return; }
    const coords = getCaretCoordinates(el, open);
    setAc({
      query: between,
      options,
      index: 0,
      openPos: open,
      top: coords.top - el.scrollTop + 22,
      left: coords.left,
    });
  }, [value, docs]);

  const insert = useCallback((doc) => {
    const el = taRef.current;
    if (!el || !ac) return;
    const caret = el.selectionStart;
    const start = ac.openPos + 2;
    const newVal = value.slice(0, start) + doc.name + "]]" + value.slice(caret);
    onChange(newVal);
    setAc(null);
    requestAnimationFrame(() => {
      const pos = start + doc.name.length + 2;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  }, [ac, value, onChange]);

  const onKeyDown = (e) => {
    if (!ac) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setAc((a) => ({ ...a, index: (a.index + 1) % a.options.length }));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setAc((a) => ({ ...a, index: (a.index - 1 + a.options.length) % a.options.length }));
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      insert(ac.options[ac.index]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setAc(null);
    }
  };

  return (
    <div className="md-editor" data-testid="md-editor">
      <div className="md-editor-source">
        <textarea
          ref={taRef}
          className="md-editor-textarea"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyUp={updateAutocomplete}
          onClick={updateAutocomplete}
          onKeyDown={onKeyDown}
          onBlur={() => setTimeout(() => setAc(null), 120)}
          data-testid="doc-editor"
          spellCheck={false}
          placeholder="Write Markdown. Type [[ to link a note, > [!note] for a callout, ![[Note]] to embed."
        />
        {ac && (
          <div
            className="md-autocomplete"
            style={{ top: ac.top, left: ac.left }}
            data-testid="wikilink-autocomplete"
          >
            <div className="md-ac-head">Link to note</div>
            {ac.options.map((d, i) => (
              <button
                key={d.id}
                type="button"
                className={`md-ac-item ${i === ac.index ? "active" : ""}`}
                onMouseDown={(e) => { e.preventDefault(); insert(d); }}
                data-testid={`wikilink-option-${i}`}
              >
                <span className="md-ac-name">{d.name}</span>
                {d.folder ? <span className="md-ac-folder">{d.folder}</span> : null}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="md-editor-preview" data-testid="md-editor-preview">
        <PropertiesPanel markdown={value} />
        <Markdown text={value} docs={docs} onOpenDoc={onOpenDoc} />
      </div>
    </div>
  );
}
